import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { TeeMapping } from '@/types/golfCourseApi';
import type { TeeBox } from '@/types';
import { getAvailableColors, hasAtLeastOneMapping } from '@/utils/teeMapping';

interface TeeMappingStepProps {
  mappings: TeeMapping[];
  onUpdateMapping: (index: number, color: TeeBox | null) => void;
  onImport: () => void;
  onBack: () => void;
  importing: boolean;
}

const TEE_COLOR_LABELS: Record<TeeBox, string> = {
  red: 'Red',
  gold: 'Gold',
  white: 'White',
  blue: 'Blue',
  black: 'Black',
};

const TeeMappingStep = ({ mappings, onUpdateMapping, onImport, onBack, importing }: TeeMappingStepProps) => {
  const canImport = hasAtLeastOneMapping(mappings);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Map each tee to a color in your database. Tees with matching names are auto-mapped.
        You can skip tees you don't need.
      </p>

      <div className="space-y-3">
        {mappings.map((mapping, index) => {
          const available = getAvailableColors(mappings, index);

          return (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{mapping.apiTeeName}</div>
                <div className="text-xs text-muted-foreground">
                  {mapping.gender !== 'Both' && `${mapping.gender} · `}
                  {mapping.totalYards ? `${mapping.totalYards.toLocaleString()} yds` : '—'}
                </div>
              </div>

              <Select
                value={mapping.dbColor ?? 'skip'}
                onValueChange={(val) => onUpdateMapping(index, val === 'skip' ? null : val as TeeBox)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip</SelectItem>
                  {available.map(color => (
                    <SelectItem key={color} value={color}>
                      {TEE_COLOR_LABELS[color]}
                    </SelectItem>
                  ))}
                  {/* If current mapping has a color that's "used" elsewhere, still show it */}
                  {mapping.dbColor && !available.includes(mapping.dbColor) && (
                    <SelectItem value={mapping.dbColor}>
                      {TEE_COLOR_LABELS[mapping.dbColor]}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      {!canImport && (
        <p className="text-sm text-amber-600">
          Map at least one tee to continue.
        </p>
      )}

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onBack} className="flex-1" disabled={importing}>
          Back
        </Button>
        <Button
          onClick={onImport}
          className="flex-1 bg-forest hover:bg-forest/90 text-white"
          disabled={!canImport || importing}
        >
          {importing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            'Import Course'
          )}
        </Button>
      </div>
    </div>
  );
};

export default TeeMappingStep;
